import { motion } from "framer-motion";
import type { Location } from "../data/types.js";

const PIN_HEIGHT = 30;
const SHOP_HEIGHT = 44;

interface Props {
  location: Location;
  isActive: boolean;
  isDimmed: boolean;
  onClick: () => void;
}

export function LocationPin({ location, isActive, isDimmed, onClick }: Props) {
  if (location.isShop) {
    return <ShopPin location={location} isActive={isActive} onClick={onClick} />;
  }

  return (
    <div
      className="absolute"
      style={{
        left: `${location.x}%`,
        top: `${location.y}%`,
        transform: `translate(-50%, -${PIN_HEIGHT}px)`,
        zIndex: isActive ? 50 : 10,
        opacity: isDimmed ? 0.35 : 1,
        transition: "opacity 0.3s ease",
        pointerEvents: "none",
      }}
    >
      <div
        className="flex flex-col items-center justify-end"
        style={{ height: PIN_HEIGHT }}
      >
        <button
          type="button"
          className="outline-none cursor-pointer rounded-full"
          style={{ pointerEvents: "auto" }}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          aria-label={`Spot ${location.number}: ${location.name}`}
        >
          <motion.div
            className="relative flex items-center justify-center rounded-full font-bold"
            initial={false}
            animate={isActive ? "active" : "idle"}
            variants={{
              idle: {
                width: 20,
                height: 20,
                backgroundColor: "#FFFFFF",
                color: "#B76E6E",
                boxShadow: "0 1px 4px rgba(0,0,0,0.2), 0 0 0 1.5px #B76E6E",
              },
              active: {
                width: 26,
                height: 26,
                backgroundColor: "#B76E6E",
                color: "#FFFFFF",
                boxShadow:
                  "0 2px 10px rgba(183,110,110,0.45), 0 0 0 2px #FFFFFF",
              },
            }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            <span className="text-[9px]">{location.number}</span>
            {location.allowNight && (
              <span className="absolute -top-1 -right-1 text-[6px] leading-none">
                🌙
              </span>
            )}
            {location.allowSakura && !location.allowNight && (
              <span className="absolute -top-1 -right-1 text-[6px] leading-none">
                🌸
              </span>
            )}
            {location.allowMomiji &&
              !location.allowNight &&
              !location.allowSakura && (
                <span className="absolute -top-1 -right-1 text-[6px] leading-none">
                  🍁
                </span>
              )}
          </motion.div>
        </button>
        <div className="w-px h-0.5 bg-kra rounded-full" />
      </div>
    </div>
  );
}

interface ShopProps {
  location: Location;
  isActive: boolean;
  onClick: () => void;
}

function ShopPin({ location, isActive, onClick }: ShopProps) {
  return (
    <div
      className="absolute"
      style={{
        left: `${location.x}%`,
        top: `${location.y}%`,
        transform: `translate(-50%, -${SHOP_HEIGHT}px)`,
        zIndex: isActive ? 60 : 20,
        pointerEvents: "none",
      }}
    >
      <div className="flex flex-col items-center">
        <button
          type="button"
          className="outline-none cursor-pointer rounded-full"
          style={{ pointerEvents: "auto" }}
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          aria-label={`KRA Shop: ${location.name}`}
        >
          <motion.div
            className="relative flex items-center justify-center rounded-full"
            initial={false}
            animate={isActive ? "active" : "idle"}
            variants={{
              idle: {
                width: 28,
                height: 28,
                backgroundColor: "#FFF8F0",
                boxShadow:
                  "0 2px 8px rgba(183,110,110,0.22), 0 0 0 1.5px #B76E6E",
                scale: 1,
              },
              active: {
                width: 32,
                height: 32,
                backgroundColor: "#B76E6E",
                boxShadow:
                  "0 4px 16px rgba(183,110,110,0.45), 0 0 0 2px #FFFFFF",
                scale: 1.05,
              },
            }}
            transition={{ type: "spring", stiffness: 400, damping: 25 }}
          >
            <span className="text-[15px] leading-none" aria-hidden="true">
              👘
            </span>
          </motion.div>
        </button>

        <div className="w-0.5 h-2 bg-kra" />
        <div className="w-1.5 h-1.5 rounded-full bg-kra shadow-sm" />

        <div className="mt-1 px-2 py-0.5 rounded-full bg-white/95 text-[9px] font-semibold text-ink-0 tracking-wide shadow-sm whitespace-nowrap">
          Kimono Rental Ann
        </div>
      </div>
    </div>
  );
}
